import { memo } from 'react'
import { useMutation } from '@tanstack/react-query'
import { z } from 'zod'
import { Button } from '@highlight/ui'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import FileGrantsIpcRenderer from '@renderer/ipc/FileGrantsIpc.renderer'
import { useDeviceUUID } from '@renderer/hooks/useDeviceUUID'
import { AttachmentCard } from '../../components/chat/attachments/attachment-card'
import { useAddChatToast } from '../../store/toasts'
import { isElectron } from '../../utils/runtimeUtils'
import { formatBytes } from '../../utils/stringUtils'

const log = GlobalLogger.bind('OfferedDownload')

/** The desktop `files` toolset's `offer_download` tool, by the name the backend gives client tools. */
export const OFFER_DOWNLOAD_TOOL_NAME = 'client_files_offer_download'

const OfferedDownloadSchema = z.object({
  relativePath: z.string().min(1),
  name: z.string().min(1),
  sizeBytes: z.number().nonnegative(),
  deviceId: z.string().min(1),
})

/** A file in a chat's scratch folder, on one of the user's computers, that the agent offered. */
export type OfferedDownload = z.infer<typeof OfferedDownloadSchema>

/** The offered file, or null while the call runs or when it failed. */
export function parseOfferedDownload(result: unknown): OfferedDownload | null {
  const parsed = OfferedDownloadSchema.safeParse(result)
  return parsed.success ? parsed.data : null
}

interface OfferedDownloadCardProps {
  file: OfferedDownload
  conversationId: string | undefined
}

/** A file the agent offered, as one card. Only the computer that holds it can save it. */
export const OfferedDownloadCard = memo(function OfferedDownloadCard({
  file,
  conversationId,
}: OfferedDownloadCardProps) {
  const thisDeviceId = useDeviceUUID()

  return (
    <div className="py-1">
      {isElectron && conversationId && file.deviceId === thisDeviceId ? (
        <SavableFile file={file} conversationId={conversationId} />
      ) : (
        <AttachmentCard name={file.name} status="On another computer" />
      )}
    </div>
  )
})

function SavableFile({ file, conversationId }: { file: OfferedDownload; conversationId: string }) {
  const addChatToast = useAddChatToast()
  const save = useMutation({
    mutationFn: () => FileGrantsIpcRenderer.saveScratchFile({ conversationId, relativePath: file.relativePath }),
    onError: (error) => {
      log.error(`Failed to save an offered file: ${String(error)}`)
      addChatToast({ message: `Could not save ${file.name}. It may have been moved or deleted.`, type: 'error' })
    },
  })

  return (
    <AttachmentCard
      name={file.name}
      status={save.isSuccess ? 'Saved to Downloads' : formatBytes(file.sizeBytes, 0)}
      action={
        <Button size="sm" variant="outline" loading={save.isPending} onClick={() => save.mutate()}>
          Download
        </Button>
      }
    />
  )
}
